/**
 * Firestore Utility Functions
 * Helper functions for common Firestore operations like subcollection deletion
 */
import { db } from '@/js/firebase';
import { collection, getDocs, writeBatch } from 'firebase/firestore';
import { logger } from './loggerService';

/**
 * Delete all documents in a subcollection
 * @param {string} parentCollection - Parent collection name (e.g., 'deliverables')
 * @param {string} parentDocId - Parent document ID
 * @param {string} subcollectionName - Subcollection name (e.g., 'discussions')
 * @returns {Promise<number>} - Number of documents deleted
 */
async function deleteSubcollection(parentCollection, parentDocId, subcollectionName) {
  try {
    const subcollectionRef = collection(db, parentCollection, parentDocId, subcollectionName);
    const snapshot = await getDocs(subcollectionRef);

    if (snapshot.empty) {
      return 0;
    }

    const batch = writeBatch(db);
    let count = 0;

    snapshot.docs.forEach((docSnapshot) => {
      batch.delete(docSnapshot.ref);
      count++;
    });

    await batch.commit();

    logger.info('[FirestoreUtils] Subcollection deleted', {
      parentCollection,
      parentDocId,
      subcollectionName,
      documentsDeleted: count
    });

    return count;
  } catch (error) {
    const errorMessage = error?.message || 'Unknown error';
    const errorDetails = {
      parentCollection,
      parentDocId,
      subcollectionName,
      error: errorMessage,
      code: error?.code
    };

    // Provide more context for permission errors
    if (error?.code === 'permission-denied') {
      logger.error('[FirestoreUtils] Failed to delete subcollection - Permission denied', errorDetails);
      logger.warn('[FirestoreUtils] Check Firestore rules for subcollection deletion permissions', {
        path: `${parentCollection}/${parentDocId}/${subcollectionName}`,
        requiredPermission: 'delete'
      });
    } else {
      logger.error('[FirestoreUtils] Failed to delete subcollection', errorDetails);
    }

    throw new Error(`Failed to delete ${subcollectionName} subcollection: ${errorMessage}`);
  }
}

/**
 * Delete multiple subcollections for a document
 * @param {string} parentCollection - Parent collection name
 * @param {string} parentDocId - Parent document ID
 * @param {string[]} subcollectionNames - Array of subcollection names to delete
 * @returns {Promise<Object>} - Object with counts per subcollection
 */
async function deleteSubcollections(parentCollection, parentDocId, subcollectionNames) {
  const results = {};
  const errors = [];

  for (const subcollectionName of subcollectionNames) {
    try {
      results[subcollectionName] = await deleteSubcollection(
        parentCollection,
        parentDocId,
        subcollectionName
      );
    } catch (error) {
      // Log error but continue with other subcollections
      results[subcollectionName] = -1; // Indicate failure
      errors.push({
        subcollection: subcollectionName,
        error: error?.message || 'Unknown error'
      });

      logger.warn('[FirestoreUtils] Subcollection deletion failed, continuing with others', {
        parentCollection,
        parentDocId,
        subcollectionName,
        error: error?.message
      });
    }
  }

  // If all subcollections failed, throw an error
  if (errors.length === subcollectionNames.length) {
    const errorMsg = `Failed to delete all subcollections: ${errors.map(e => e.subcollection).join(', ')}`;
    logger.error('[FirestoreUtils] All subcollection deletions failed', {
      parentCollection,
      parentDocId,
      errors
    });
    throw new Error(errorMsg);
  }

  // If some failed, log warning but don't throw
  if (errors.length > 0) {
    logger.warn('[FirestoreUtils] Some subcollection deletions failed', {
      parentCollection,
      parentDocId,
      successCount: subcollectionNames.length - errors.length,
      failureCount: errors.length,
      errors
    });
  }

  return results;
}

/**
 * Delete a deliverable and all its subcollections (discussions, activityLogs)
 * @param {string} deliverableId - Deliverable document ID
 * @returns {Promise<Object>} - Deletion results
 */
export async function deleteDeliverableSubcollections(deliverableId) {
  return deleteSubcollections('deliverables', deliverableId, ['discussions', 'activityLogs']);
}

/**
 * Delete a calendar event and all its subcollections (productionSchedules, activity)
 * @param {string} eventId - Calendar event document ID
 * @returns {Promise<Object>} - Deletion results
 */
export async function deleteCalendarEventSubcollections(eventId) {
  return deleteSubcollections('calendar', eventId, ['productionSchedules', 'activity']);
}

/**
 * Delete a review and all its subcollections (items, comments, viewers)
 * @param {string} reviewId - Review document ID
 * @returns {Promise<Object>} - Deletion results
 */
export async function deleteReviewSubcollections(reviewId) {
  return deleteSubcollections('reviews', reviewId, ['items', 'comments', 'viewers']);
}

/**
 * Delete a project and all its subcollections (activityLogs, productionLogs)
 * @param {string} projectId - Project document ID
 * @returns {Promise<Object>} - Deletion results
 */
export async function deleteProjectSubcollections(projectId) {
  return deleteSubcollections('projects', projectId, ['activityLogs', 'productionLogs']);
}

/**
 * Batch delete subcollections for multiple documents
 * Useful when deleting related documents (e.g., all deliverables for a project)
 * @param {string} parentCollection - Parent collection name
 * @param {string[]} parentDocIds - Array of parent document IDs
 * @param {string[]} subcollectionNames - Array of subcollection names to delete
 * @returns {Promise<Object>} - Total counts per subcollection
 */
export async function batchDeleteSubcollections(parentCollection, parentDocIds, subcollectionNames) {
  const totalResults = {};
  subcollectionNames.forEach(name => {
    totalResults[name] = 0;
  });

  for (const docId of parentDocIds) {
    const results = await deleteSubcollections(parentCollection, docId, subcollectionNames);
    for (const [name, count] of Object.entries(results)) {
      totalResults[name] += count;
    }
  }

  logger.info('[FirestoreUtils] Batch subcollection deletion complete', {
    parentCollection,
    documentCount: parentDocIds.length,
    results: totalResults
  });

  return totalResults;
}
